// Two-step CTA the customer drives the configurator with:
//
//   1. "Calculate"          — disabled until contact details + machine are
//                             in. Click → upserts the HubSpot Contact
//                             (lead-capture) and fetches the live preview.
//                             Even if the customer bails after seeing the
//                             total, sales has them in HubSpot.
//
//   2. "Email me the quote" — replaces "Calculate" once calculation is
//                             done. Click → creates the Deal + Line Items
//                             + published Quote on the captured contact.
//
// Disabled-state hint explains what's missing so the customer isn't
// guessing why the button won't respond.
//
// While submitting (HubSpot quote-generation can take 5-15s depending
// on the chain of API calls), the button morphs into an animated
// progress bar with step-of-N status text so the customer sees
// something happening and doesn't think the page froze.

import { useEffect, useState } from "react";

import { useConfigurator } from "../../store";
import { OUTSIDE_LOWER48_MESSAGE } from "../../format";
import { RunningEstimateCompact } from "../RunningEstimate";

/** Shared button state for both the inline (desktop) SubmitSection and the
 *  mobile sticky bar, so the two never drift. Owns the disabled-reason text
 *  and the Calculate → Email-me-the-quote morph (label / click / enabled all
 *  change in lockstep). Each caller gets its own `calculating` flag, which is
 *  fine because only one surface is visible per breakpoint. */
function useSubmitButton() {
  const {
    state,
    canCalculate,
    canEmailQuote,
    hasShippingAddress,
    outsideLower48,
    calculate,
    submitQuote,
  } = useConfigurator();
  const [calculating, setCalculating] = useState(false);

  let reason: string | null = null;
  if (!state.selectedMachineId) reason = "Choose a machine first";
  else if (!hasShippingAddress) reason = "Enter your delivery address";
  else if (!state.customer.firstName.trim() || !state.customer.lastName.trim())
    reason = "Enter your name";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.customer.email.trim()))
    reason = "Enter a valid email";

  let label: string;
  let onClick: () => void;
  let enabled: boolean;
  if (!state.calculated) {
    label = calculating ? "Calculating…" : "Calculate";
    enabled = canCalculate && !calculating;
    onClick = async () => {
      setCalculating(true);
      try {
        await calculate();
      } finally {
        setCalculating(false);
      }
    };
  } else {
    label = "Email me the quote";
    enabled = canEmailQuote;
    onClick = () => void submitQuote(true);
  }

  // Employees get a create-only CTA in the submit phase.
  const employeeSubmit = state.employee.active && state.calculated;

  return {
    state,
    reason,
    label,
    onClick,
    enabled,
    calculating,
    employeeSubmit,
    outsideLower48,
    submitNoEmail: () => void submitQuote(false),
  };
}

/** Outside the lower-48: shipping needs a manual quote, but we still want the
 *  sale — so capture the shopper as a Sales lead and confirm follow-up. */
function OutsideLower48Notice() {
  const { state, captureSalesLead } = useConfigurator();
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">(
    "idle",
  );
  const c = state.customer;
  const canRequest =
    Boolean(c.firstName.trim()) &&
    Boolean(c.lastName.trim()) &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email.trim());

  if (status === "done") {
    return (
      <div className="rounded-lg border border-amber bg-[#FFF7E8] px-4 py-3 font-sans text-[14px] leading-relaxed text-ink">
        ✓ Thanks{c.firstName ? `, ${c.firstName}` : ""}! Gammill Sales will
        contact you about pricing and shipping for your area.
      </div>
    );
  }

  async function request() {
    setStatus("sending");
    try {
      await captureSalesLead();
      setStatus("done");
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber bg-[#FFF7E8] px-4 py-3">
      <p className="font-sans text-[14px] leading-relaxed text-ink">
        {OUTSIDE_LOWER48_MESSAGE}
      </p>
      <button
        type="button"
        onClick={request}
        disabled={!canRequest || status === "sending"}
        className="inline-flex items-center justify-center rounded-full bg-ink px-5 py-3 font-sans text-[15px] font-bold text-white transition hover:bg-graphite disabled:cursor-not-allowed disabled:bg-mist disabled:text-steel"
      >
        {status === "sending" ? "Sending…" : "Have Sales contact me"}
      </button>
      {!canRequest && (
        <p className="font-sans text-[13px] text-steel">
          ⓘ Add your name and email above so Sales can reach you.
        </p>
      )}
      {status === "error" && (
        <p className="font-sans text-[13px] text-red-700">
          Couldn't send just now — please call 417-256-5919.
        </p>
      )}
    </div>
  );
}

const BUTTON_CLASS =
  "inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber px-6 py-4 font-sans text-base font-bold tracking-[0.02em] text-ink shadow-[0_6px_18px_rgba(250,173,61,0.32)] transition hover:bg-amber-hover disabled:cursor-not-allowed disabled:bg-mist disabled:text-steel disabled:shadow-none";

const SPLIT_BASE =
  "inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-4 font-sans text-[15px] font-bold tracking-[0.02em] transition disabled:cursor-not-allowed disabled:opacity-50";

/** Submit CTA — "Email me the quote" for customers, or "Create Deal & Quote"
 *  (no email) for logged-in employees in the submit phase. The employee
 *  "Create + Email" button was removed 2026-10-06 — it wasn't reliable. */
function SubmitCta({
  employeeSubmit,
  enabled,
  onClick,
  label,
  submitNoEmail,
}: {
  employeeSubmit: boolean;
  enabled: boolean;
  onClick: () => void;
  label: string;
  submitNoEmail: () => void;
}) {
  if (employeeSubmit) {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={submitNoEmail}
          disabled={!enabled}
          className={SPLIT_BASE + " bg-ink text-white hover:bg-graphite"}
        >
          Create Deal &amp; Quote
        </button>
      </div>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={!enabled} className={BUTTON_CLASS}>
      {label}
    </button>
  );
}

export function SubmitSection() {
  const {
    state,
    reason,
    label,
    onClick,
    enabled,
    calculating,
    employeeSubmit,
    outsideLower48,
    submitNoEmail,
  } = useSubmitButton();

  // While the quote is being created, show a SubmitProgress widget
  // instead of the button. The widget owns the spinner / progress bar /
  // step-of-N text — keeps button logic clean.
  if (state.submitting) {
    return (
      <div className="flex flex-col gap-3 border-t border-line pt-6">
        <SubmitProgress />
      </div>
    );
  }

  if (outsideLower48) {
    return (
      <div className="flex flex-col gap-3 border-t border-line pt-6">
        <OutsideLower48Notice />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-6">
      <SubmitCta
        employeeSubmit={employeeSubmit}
        enabled={enabled}
        onClick={onClick}
        label={label}
        submitNoEmail={submitNoEmail}
      />
      {reason && !state.calculated && !calculating && (
        <p className="font-sans text-[14px] text-steel">ⓘ {reason}</p>
      )}
      {state.calculated && (
        <p className="font-serif text-[14px] italic text-steel">
          {employeeSubmit ? (
            <>
              “Create Deal &amp; Quote” creates the deal and quote in HubSpot
              without emailing the customer.
            </>
          ) : (
            <>
              Your contact details are saved. Clicking sends your quote to{" "}
              <span className="font-semibold text-ink">{state.customer.email}</span>.
            </>
          )}
        </p>
      )}
      {state.submitError && (
        <p className="border-l-[3px] border-red-400 pl-4 font-sans text-[14px] text-red-700">
          {state.submitError}
        </p>
      )}
    </div>
  );
}

/** Mobile-only sticky CTA. Pinned to the bottom of the viewport so the
 *  Calculate / Email-me-the-quote action is always reachable without
 *  scrolling to the end of the (long) config form — the #1 reason mobile
 *  shoppers were missing it. Hidden on desktop (`lg:hidden`), where the
 *  inline SubmitSection in the right column is already in view.
 *
 *  Mirrors every state the inline button has — disabled reason, in-flight
 *  progress, submit error — so it can fully stand in for the inline button,
 *  which we hide on mobile to avoid two competing CTAs.
 *
 *  Note: the host page's Emily chat bubble floats bottom-LEFT with its own
 *  (higher) z-index, so it may visually overlap the left end of this bar on
 *  narrow screens. z-40 keeps the bar above page content but below modals
 *  (z-50). */
export function MobileSubmitBar() {
  const {
    state,
    reason,
    label,
    onClick,
    enabled,
    calculating,
    employeeSubmit,
    outsideLower48,
    submitNoEmail,
  } = useSubmitButton();

  // The Confirmation screen replaces the whole config view — no CTA there.
  if (state.generated) return null;

  if (outsideLower48) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.625rem)] pt-3 shadow-[0_-6px_18px_rgba(0,0,0,0.10)] backdrop-blur lg:hidden">
        <OutsideLower48Notice />
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.625rem)] pt-3 shadow-[0_-6px_18px_rgba(0,0,0,0.10)] backdrop-blur lg:hidden">
      {state.submitting ? (
        <SubmitProgress />
      ) : (
        <>
          <RunningEstimateCompact />
          {state.submitError && (
            <p className="mb-2 border-l-[3px] border-red-400 pl-3 font-sans text-[13px] text-red-700">
              {state.submitError}
            </p>
          )}
          <SubmitCta
            employeeSubmit={employeeSubmit}
            enabled={enabled}
            onClick={onClick}
            label={label}
            submitNoEmail={submitNoEmail}
          />
          {reason && !state.calculated && !calculating ? (
            <p className="mt-1.5 text-center font-sans text-[13px] text-steel">
              ⓘ {reason}
            </p>
          ) : state.calculated && !employeeSubmit ? (
            <p className="mt-1.5 text-center font-serif text-[13px] italic text-steel">
              Sends your quote to{" "}
              <span className="font-semibold text-ink">{state.customer.email}</span>
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Animated progress widget shown while HubSpot is building the
 *  quote. Visual goals:
 *    – Make the wait feel intentional (a process is happening)
 *    – Don't lie about being done — cap at ~92% if the API is slow
 *    – Cycle through plausible status messages so the page feels
 *      alive instead of frozen
 *
 *  The bar uses a real ~10-second easing toward 92% then sits there
 *  until the API actually returns; when state.submitting flips false
 *  the parent unmounts this widget and the Confirmation takes over. */
function SubmitProgress() {
  // 0-100. Animates 0 → 92 over the durations below, then waits.
  // 92% (not 100%) so the customer knows we haven't actually finished
  // until the UI swaps over to the Confirmation.
  const [pct, setPct] = useState(2);
  const [stepIdx, setStepIdx] = useState(0);

  // Plausible-sequence labels that align roughly with the backend
  // pipeline (contact upsert → deal → line items → quote → publish).
  const steps = [
    "Saving your contact details…",
    "Creating your deal in our system…",
    "Adding your line items…",
    "Building your quote…",
    "Publishing and finalizing…",
  ];

  // Bar fill ramp — front-loaded so the bar appears responsive early,
  // then slows as we approach 92% so a slow HubSpot day doesn't look
  // suspicious. Each step is ~2s.
  useEffect(() => {
    const targets = [25, 45, 65, 82, 92];
    let cancelled = false;
    let i = 0;
    function step() {
      if (cancelled) return;
      setPct(targets[i]);
      setStepIdx(i);
      i++;
      if (i < targets.length) setTimeout(step, 2000);
    }
    step();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-full bg-mist p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="flex items-center gap-2 font-sans text-[14px] font-semibold text-ink">
          <Spinner />
          {steps[stepIdx]}
        </p>
        <span className="font-sans text-[12px] tabular-nums text-steel">
          {pct}%
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Generating your quote"
        className="h-2 w-full overflow-hidden rounded-full bg-line"
      >
        <div
          className="h-full bg-amber transition-[width] duration-[1800ms] ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Small spinning circle next to the status text. Pure CSS, no JS
 *  animation cost. */
function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-line border-t-amber"
    />
  );
}
